import React from 'react';
import { cn } from '@veevarts/design-system';
import { Icon } from '@iconify/react';
import OfflineBadge from './OfflineBadge';

interface ServiceCardProps {
  icon: string;
  title: string;
  description: string;
  onPress: () => void;
  className?: string;
  selected?: boolean;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * Touch-optimised service card for the module selector.
 * When disabled, shows an OfflineBadge overlay and dims the card.
 * Minimum touch target: 280px height.
 * Uses iconify icon string instead of LucideIcon component.
 */
const ServiceCard: React.FC<ServiceCardProps> = ({
  icon,
  title,
  description,
  onPress,
  className,
  selected = false,
  disabled = false,
  loading = false,
}) => {
  const isInactive = disabled || loading;
  return (
    <button
      onClick={isInactive ? undefined : onPress}
      disabled={isInactive}
      aria-disabled={isInactive}
      aria-busy={loading}
      className={cn(
        'relative flex flex-col items-center justify-center gap-4 p-8',
        'min-h-[280px] w-full rounded-2xl border bg-content1',
        'transition-all duration-200 kiosk-card-press',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        selected
          ? 'border-primary bg-primary-50 ring-1 ring-primary/20'
          : 'border-divider hover:border-primary/30',
        loading && 'scale-[0.97] border-primary ring-2 ring-primary/40 shadow-lg shadow-primary/20',
        disabled && !loading && 'opacity-50 cursor-not-allowed pointer-events-none',
        className,
      )}
      style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
    >
      {disabled && <OfflineBadge />}

      <div
        className={cn(
          'flex items-center justify-center w-14 h-14 rounded-2xl transition-all duration-200',
          selected ? 'bg-primary text-primary-foreground' : 'bg-default-100 text-primary',
        )}
      >
        <Icon icon={icon} width={36} height={36} aria-hidden="true" />
      </div>

      <div className="text-center">
        <h3 className="text-lg font-semibold text-foreground mb-1">{title}</h3>
        <p className="text-sm text-default-500 leading-relaxed">{description}</p>
      </div>
    </button>
  );
};

export default ServiceCard;
